<!-- eslint-disable vue/max-len -->
<template lang="pug">
app-main-content(v-if="isAuthorizedUser")
  .dashboard-container
    .dashboard-header
      .welcome-section
        h1.welcome-title Welcome, {{ userName }}!
        p.welcome-subtitle Check out the impact and magic you are making in the community.
      .header-actions
        el-date-picker(
          v-model="dateRange"
          type="daterange"
          range-separator="To"
          start-placeholder="Start date"
          end-placeholder="End date"
          size="default"
          format="MMM DD"
          value-format="YYYY-MM-DD"
          @change="onDateRangeChange"
          :default-value="defaultDateRange"
        )

    .loading-state(v-if="metricsData.loading")
      .loading-message Loading metrics...

    .error-state(v-else-if="metricsData.error")
      .error-message {{ metricsData.error }}

    .dashboard-content(v-else)
      //- Key Metrics - Top 4 Cards
      .key-metrics-grid
        .key-metric-card(v-for="metric in keyMetrics" :key="metric.id")
          .metric-icon-wrapper(:class="metric.colorClass")
            fai(type="fal" :icon="metric.icon").metric-icon
          .metric-content
            .metric-label {{ metric.label }}
            .metric-value-row
              .metric-value {{ metric.value }}
              .metric-change(v-if="metric.change" :class="metric.changeClass") {{ metric.change }}

      //- Two-Column Layout
      .dashboard-main-layout
        //- Left Column (2/3)
        .dashboard-left
          //- Community Overview
          el-card.section-card
            h2.section-title Community Overview
            .stats-grid
              .stat-item(v-for="stat in communityStats" :key="stat.id")
                .stat-icon-wrapper(:class="stat.colorClass")
                  fai(type="fal" :icon="stat.icon").stat-icon
                .stat-content
                  .stat-label {{ stat.label }}
                  .stat-value {{ stat.value }}

          //- Social Activity
          el-card.section-card
            h2.section-title Social Activity
            .social-stats-grid
              .stat-item(v-for="stat in socialActivityStats" :key="stat.id")
                .stat-icon-wrapper(:class="stat.colorClass")
                  fai(type="fal" :icon="stat.icon").stat-icon
                .stat-content
                  .stat-label {{ stat.label }}
                  .stat-value {{ stat.value }}

          //- Most Supported Stickers
          el-card.section-card(
            v-if="metricsData.mostSupportedStickers.length > 0"
          )
            h2.section-title Most Supported Stickers
            .stickers-grid
              .sticker-card(
                v-for="sticker in metricsData.mostSupportedStickers"
                :key="sticker.stickerId"
              )
                .sticker-info
                  .sticker-name {{ sticker.stickerName }}
                  .sticker-stats {{ sticker.salesCount }} sales
                .sticker-amount ${{ formatNumber(sticker.salesAmount) }}

          //- Transaction Analytics
          el-card.section-card
            h2.section-title Transaction Analytics
            .analytics-grid
              .analytics-item(v-for="item in transactionAnalytics" :key="item.id")
                .analytics-label {{ item.label }}
                .analytics-value {{ item.value }}
                .analytics-meta {{ item.meta }}

          //- Revenue Analytics
          el-card.section-card
            h2.section-title Revenue Analytics
            .analytics-grid-4
              .analytics-item(v-for="item in revenueAnalytics" :key="item.id")
                .analytics-label {{ item.label }}
                .analytics-value {{ item.value }}
                .analytics-meta {{ item.meta }}

          //- Marketplace Analytics
          el-card.section-card
            h2.section-title Marketplace Analytics
            .analytics-grid
              .analytics-item(v-for="item in marketplaceAnalytics" :key="item.id")
                .analytics-label {{ item.label }}
                .analytics-value {{ item.value }}
                .analytics-meta {{ item.meta }}

        //- Right Sidebar (1/3)
        .dashboard-right
          //- Recent Activity
          el-card.section-card(v-if="recentSupporters.length > 0")
            .section-header
              h2.section-title Recent Activity
              el-button(text size="small" @click="navigateToActivity").view-all-btn
                span View All
                fai(type="fal" icon="chevron-right").ms-1
            .activity-list
              .activity-item(v-for="supporter in recentSupporters" :key="supporter.transactionId")
                .activity-avatar
                  .avatar-circle {{ supporter.initials }}
                .activity-content
                  .activity-header
                    span.activity-name {{ supporter.purchaserName }}
                    span.activity-dot
                    span.activity-time {{ supporter.timeAgo }}
                  .activity-description
                    | {{ supporter.description }}
                    span.activity-amount(v-if="supporter.totalAmount")  ${{ supporter.totalAmount }}
                .activity-indicator(:class="supporter.indicatorClass")

          //- Referral Conversions
          el-card.section-card(
            v-if="metricsData.referralConversions.length > 0"
          )
            h2.section-title Referral Conversions
            .referrals-list
              .referral-item(
                v-for="(referral, index) in metricsData.referralConversions"
                :key="index"
              )
                .referral-name {{ referral.referralName }}
                .referral-amount ${{ formatNumber(referral.salesAmount) }}

</template>

<script lang="ts">
import {
  defineComponent, computed, onMounted, ref, reactive, watch,
} from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { ElCard } from 'element-plus';
import globalShared from '@/shared';
import { findOrganizationMetrics } from '@/graphs/gql';

export default defineComponent({
  name: 'DashboardView',
  components: {
    ElCard,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const { makeGraphRequest, getAuthToken } = globalShared();

    // User role detection
    const isSuperAdmin = computed(() => store.getters['users/isAdmin']);
    const isOrgAdmin = computed(() => store.getters['users/isOrgAdmin']);

    const isAuthorizedUser = computed(() => isSuperAdmin.value || isOrgAdmin.value);

    // Reactive state for metrics data
    const metricsData = reactive({
      loading: false,
      error: null as string | null,
      followersCount: [] as Array<{ date: string; new: number; cumulative: number }>,
      salesAmountSum: [] as Array<{ date: string; new: number; cumulative: number }>,
      donationsReceived: [] as Array<{ date: string; new: number; cumulative: number }>,
      alliesCount: [] as Array<{ date: string; new: number; cumulative: number }>,
      affiliatesCount: [] as Array<{ date: string; new: number; cumulative: number }>,
      stickersCount: [] as Array<{ date: string; new: number; cumulative: number }>,
      campaignsCount: [] as Array<{ date: string; new: number; cumulative: number }>,
      ticketsCount: [] as Array<{ date: string; new: number; cumulative: number }>,
      sweepstakesCount: [] as Array<{ date: string; new: number; cumulative: number }>,
      // eslint-disable-next-line max-len
      salesCountSumFromSocialShops: [] as Array<{ date: string; new: number; cumulative: number }>,
      // eslint-disable-next-line max-len
      salesAmountSumFromSocialShops: [] as Array<{ date: string; new: number; cumulative: number }>,
      // eslint-disable-next-line max-len
      productsAddedToSocialShopsCount: [] as Array<{
        date: string; new: number; cumulative: number;
      }>,
      lastFollowersCount: { date: '', new: 0, cumulative: 0 },
      lastSalesAmountSum: { date: '', new: 0, cumulative: 0 },
      lastDonationReceived: { date: '', new: 0, cumulative: 0 },
      lastAlliesCount: { date: '', new: 0, cumulative: 0 },
      lastAffiliatesCount: { date: '', new: 0, cumulative: 0 },
      lastStickersCount: { date: '', new: 0, cumulative: 0 },
      lastCampaignsCount: { date: '', new: 0, cumulative: 0 },
      lastTicketsCount: { date: '', new: 0, cumulative: 0 },
      lastSweepstakesCount: { date: '', new: 0, cumulative: 0 },
      lastSalesCountSumFromSocialShops: { date: '', new: 0, cumulative: 0 },
      lastSalesAmountSumFromSocialShops: { date: '', new: 0, cumulative: 0 },
      lastProductsAddedToSocialShopsCount: { date: '', new: 0, cumulative: 0 },
      moneyLeftInTheAccount: { amountInWallet: 0, hasWallet: false },
      recentSupporters: [] as Array<any>,
      mostSupportedStickers: [] as Array<any>,
      referralConversions: [] as Array<any>,
    });

    // Get current organization ID
    const currentOrgId = computed(() => {
      const selectedOrg = store.getters['organizations/GET_SELECTED_ORGANIZATION'];
      if (selectedOrg?.id) {
        return selectedOrg.id;
      }

      // Fallback to user's first organization if available
      const me = store.getters['users/me'];
      if (me?.organizations && me.organizations.length > 0) {
        return me.organizations[0].id;
      }

      return null;
    });

    // User name
    const userName = computed(() => {
      const me = store.getters['users/me'];
      return me?.firstName || 'there';
    });

    // Format number with commas
    const formatNumber = (num: number) => new Intl.NumberFormat('en-US').format(num);

    // Format currency
    const formatCurrency = (amount: number) => new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

    // Calculate percentage change
    const calculatePercentageChange = (timeSeries: Array<{ new: number }>) => {
      if (timeSeries.length < 2) return null;
      const recent = timeSeries.slice(-3).reduce((sum, item) => sum + item.new, 0);
      const previous = timeSeries.slice(-6, -3).reduce((sum, item) => sum + item.new, 0);
      if (previous === 0) return null;
      const change = ((recent - previous) / previous) * 100;
      return change > 0 ? `↑ ${Math.round(change)}%` : `↓ ${Math.abs(Math.round(change))}%`;
    };

    // Date range functionality
    const getDefaultDateRange = () => {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - 6); // Last 7 days (including today)
      return [
        startDate.toISOString().split('T')[0],
        endDate.toISOString().split('T')[0],
      ];
    };

    // Validate UUID format
    const isValidUUID = (uuid: string) => {
      // eslint-disable-next-line max-len
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      return uuidRegex.test(uuid);
    };

    // Format date for GraphQL (keep as YYYY-MM-DD format)
    const formatDateForGraphQL = (dateString: string) => {
      // Ensure the date is in YYYY-MM-DD format and sanitize it
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) {
        throw new Error(`Invalid date: ${dateString}`);
      }
      return dateString; // Keep the YYYY-MM-DD format
    };

    // Sanitize organization ID for string interpolation
    const sanitizeOrgId = (orgId: string) => {
      // Remove any potential injection characters and validate UUID format
      if (!isValidUUID(orgId)) {
        throw new Error(`Invalid organization ID format: ${orgId}`);
      }
      return orgId.replace(/[^a-f0-9-]/gi, ''); // Only allow UUID characters
    };

    const dateRange = ref(getDefaultDateRange());
    const defaultDateRange = getDefaultDateRange();

    // Get initials from name
    const getInitials = (name: string) => {
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    };

    // Calculate time ago
    const getTimeAgo = (dateString: string) => {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 60) return `${diffMins} min ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    };

    // Fetch organization metrics
    const fetchMetrics = async () => {
      if (!currentOrgId.value) {
        return;
      }

      if (!isValidUUID(currentOrgId.value)) {
        metricsData.error = 'Invalid organization ID format';
        return;
      }

      metricsData.loading = true;
      metricsData.error = null;

      try {
        const orgId = sanitizeOrgId(currentOrgId.value);
        const startDate = formatDateForGraphQL(dateRange.value[0]);
        const endDate = formatDateForGraphQL(dateRange.value[1]);

        // Create the query with string interpolation
        const query = findOrganizationMetrics(orgId, startDate, endDate);

        const response = await makeGraphRequest(
          'query',
          query,
          {}, // No variables needed since they're interpolated into the query
          getAuthToken(),
        );

        if (response?.data?.findOrganizationMetrics) {
          const metrics = response.data.findOrganizationMetrics;

          // Update metrics data - time series
          metricsData.followersCount = metrics.followersCount || [];
          metricsData.salesAmountSum = metrics.salesAmountSum || [];
          metricsData.donationsReceived = metrics.donationsReceived || [];
          metricsData.alliesCount = metrics.alliesCount || [];
          metricsData.affiliatesCount = metrics.affiliatesCount || [];
          metricsData.stickersCount = metrics.stickersCount || [];
          metricsData.campaignsCount = metrics.campaignsCount || [];
          metricsData.ticketsCount = metrics.ticketsCount || [];
          metricsData.sweepstakesCount = metrics.sweepstakesCount || [];
          // eslint-disable-next-line max-len
          metricsData.salesCountSumFromSocialShops = metrics.salesCountSumFromSocialShops || [];
          // eslint-disable-next-line max-len
          metricsData.salesAmountSumFromSocialShops = metrics.salesAmountSumFromSocialShops
            || [];
          // eslint-disable-next-line max-len
          metricsData.productsAddedToSocialShopsCount = metrics
            .productsAddedToSocialShopsCount || [];

          // Update last values
          metricsData.lastFollowersCount = metrics.lastFollowersCount || { date: '', new: 0, cumulative: 0 };
          metricsData.lastSalesAmountSum = metrics.lastSalesAmountSum || { date: '', new: 0, cumulative: 0 };
          metricsData.lastDonationReceived = metrics.lastDonationReceived || { date: '', new: 0, cumulative: 0 };
          metricsData.lastAlliesCount = metrics.lastAlliesCount || { date: '', new: 0, cumulative: 0 };
          metricsData.lastAffiliatesCount = metrics.lastAffiliatesCount || { date: '', new: 0, cumulative: 0 };
          metricsData.lastStickersCount = metrics.lastStickersCount || { date: '', new: 0, cumulative: 0 };
          metricsData.lastCampaignsCount = metrics.lastCampaignsCount || { date: '', new: 0, cumulative: 0 };
          metricsData.lastTicketsCount = metrics.lastTicketsCount || { date: '', new: 0, cumulative: 0 };
          // eslint-disable-next-line max-len
          metricsData.lastSweepstakesCount = metrics.lastSweepstakesCount || { date: '', new: 0, cumulative: 0 };
          // eslint-disable-next-line max-len
          metricsData.lastSalesCountSumFromSocialShops = metrics.lastSalesCountSumFromSocialShops || { date: '', new: 0, cumulative: 0 };
          // eslint-disable-next-line max-len
          metricsData.lastSalesAmountSumFromSocialShops = metrics
            .lastSalesAmountSumFromSocialShops || { date: '', new: 0, cumulative: 0 };
          // eslint-disable-next-line max-len
          metricsData.lastProductsAddedToSocialShopsCount = metrics
            .lastProductsAddedToSocialShopsCount || { date: '', new: 0, cumulative: 0 };

          // Update other data
          metricsData.moneyLeftInTheAccount = metrics.moneyLeftInTheAccount
            || { amountInWallet: 0, hasWallet: false };
          metricsData.recentSupporters = metrics.recentSupporters || [];
          metricsData.mostSupportedStickers = metrics.mostSupportedStickers || [];
          metricsData.referralConversions = metrics.referralConversions || [];
        } else if (response?.errors) {
          metricsData.error = `GraphQL Error: ${response.errors[0]?.message || 'Unknown error'}`;
        } else {
          metricsData.error = 'No data received from server';
        }
      } catch (error) {
        metricsData.error = `Failed to load metrics data: ${error.message || error}`;
      } finally {
        metricsData.loading = false;
      }
    };

    onMounted(() => {
      if (!isAuthorizedUser.value) {
        router.push('/web');
      } else {
        fetchMetrics();
      }
    });

    // Current metric values
    const currentDonations = computed(
      () => metricsData.lastDonationReceived.cumulative || 0,
    );
    const currentFollowers = computed(
      () => metricsData.lastFollowersCount.cumulative || 0,
    );
    const currentAllies = computed(() => metricsData.lastAlliesCount.cumulative || 0);
    const currentAffiliates = computed(
      () => metricsData.lastAffiliatesCount.cumulative || 0,
    );
    const currentCampaigns = computed(
      () => metricsData.lastCampaignsCount.cumulative || 0,
    );
    const currentSocialShopProducts = computed(
      () => metricsData.lastProductsAddedToSocialShopsCount.cumulative || 0,
    );
    const currentWalletBalance = computed(
      () => metricsData.moneyLeftInTheAccount.amountInWallet || 0,
    );

    // Key Metrics (Top 4 Cards)
    const keyMetrics = computed(() => [
      {
        id: 'donations',
        label: 'TOTAL DONATIONS',
        value: formatCurrency(currentDonations.value),
        change: calculatePercentageChange(metricsData.donationsReceived),
        changeClass: 'change-positive',
        icon: 'heart',
        colorClass: 'icon-teal',
      },
      {
        id: 'followers',
        label: 'ACTIVE USERS',
        value: formatNumber(currentFollowers.value),
        change: calculatePercentageChange(metricsData.followersCount),
        changeClass: 'change-positive',
        icon: 'users',
        colorClass: 'icon-purple',
      },
      {
        id: 'wallet',
        label: 'WALLET BALANCE',
        value: formatCurrency(currentWalletBalance.value),
        change: null,
        changeClass: 'change-positive',
        icon: 'wallet',
        colorClass: 'icon-yellow',
      },
      {
        id: 'campaigns',
        label: 'TOTAL CAMPAIGNS',
        value: formatNumber(currentCampaigns.value),
        change: calculatePercentageChange(metricsData.campaignsCount),
        changeClass: 'change-positive',
        icon: 'chart-line',
        colorClass: 'icon-pink',
      },
    ]);

    // Community Stats (6 items)
    const communityStats = computed(() => [
      {
        id: 'allies',
        label: 'ALLIES',
        value: formatNumber(currentAllies.value),
        icon: 'award',
        colorClass: 'icon-teal',
      },
      {
        id: 'affiliates',
        label: 'AFFILIATES',
        value: formatNumber(currentAffiliates.value),
        icon: 'handshake',
        colorClass: 'icon-yellow',
      },
      {
        id: 'campaigns',
        label: 'CAMPAIGNS',
        value: formatNumber(currentCampaigns.value),
        icon: 'bullhorn',
        colorClass: 'icon-blue',
      },
      {
        id: 'stickers',
        label: 'STICKERS',
        value: formatNumber(metricsData.lastStickersCount.cumulative || 0),
        icon: 'sparkles',
        colorClass: 'icon-pink',
      },
      {
        id: 'followers',
        label: 'FOLLOWERS',
        value: formatNumber(currentFollowers.value),
        icon: 'users',
        colorClass: 'icon-purple',
      },
      {
        id: 'products',
        label: 'SHOP PRODUCTS',
        value: formatNumber(currentSocialShopProducts.value),
        icon: 'shopping-bag',
        colorClass: 'icon-orange',
      },
    ]);

    // Social Activity Stats (5 items)
    const socialActivityStats = computed(() => {
      const socialShopSales = metricsData
        .lastSalesCountSumFromSocialShops.cumulative || 0;
      const socialShopRevenue = metricsData
        .lastSalesAmountSumFromSocialShops.cumulative || 0;
      const tickets = metricsData.lastTicketsCount.cumulative || 0;
      const sweepstakes = metricsData.lastSweepstakesCount.cumulative || 0;
      return [
        {
          id: 'social-shop-sales',
          label: 'SOCIAL SHOP SALES',
          value: formatNumber(socialShopSales),
          icon: 'shopping-cart',
          colorClass: 'icon-purple',
        },
        {
          id: 'social-shop-revenue',
          label: 'SHOP REVENUE',
          value: formatCurrency(socialShopRevenue),
          icon: 'dollar-sign',
          colorClass: 'icon-teal',
        },
        {
          id: 'tickets',
          label: 'TICKETS',
          value: formatNumber(tickets),
          icon: 'ticket',
          colorClass: 'icon-blue',
        },
        {
          id: 'sweepstakes',
          label: 'SWEEPSTAKES',
          value: formatNumber(sweepstakes),
          icon: 'trophy',
          colorClass: 'icon-yellow',
        },
      ];
    });

    // Transaction Analytics
    const transactionAnalytics = computed(() => {
      const totalSales = metricsData.lastSalesAmountSum.cumulative || 0;
      const socialShopSales = metricsData
        .lastSalesAmountSumFromSocialShops.cumulative || 0;
      const donationsTotal = currentDonations.value;
      const stickersActive = metricsData.lastStickersCount.cumulative || 0;
      const campaignsActive = currentCampaigns.value;
      const ticketsTotal = metricsData.lastTicketsCount.cumulative || 0;

      return [
        {
          id: 'total-sales',
          label: 'Total Sales',
          value: formatCurrency(totalSales),
          meta: calculatePercentageChange(metricsData.salesAmountSum) || 'All time',
        },
        {
          id: 'wallet-balance',
          label: 'Wallet Balance',
          value: formatCurrency(currentWalletBalance.value),
          meta: 'In reserve',
        },
        {
          id: 'social-shop-sales',
          label: 'Social Shop Sales',
          value: formatCurrency(socialShopSales),
          meta: calculatePercentageChange(
            metricsData.salesAmountSumFromSocialShops,
          ) || 'All time',
        },
        {
          id: 'donations',
          label: 'Donations',
          value: formatCurrency(donationsTotal),
          meta: `${formatNumber(
            metricsData.donationsReceived.reduce((sum, item) => sum + item.new, 0),
          )} donations`,
        },
        {
          id: 'active-campaigns',
          label: 'Active Campaigns',
          value: formatNumber(campaignsActive),
          meta: `${formatNumber(stickersActive)} stickers`,
        },
        {
          id: 'tickets-sold',
          label: 'Tickets Sold',
          value: formatNumber(ticketsTotal),
          meta: calculatePercentageChange(metricsData.ticketsCount) || 'All time',
        },
      ];
    });

    // Revenue Analytics
    const revenueAnalytics = computed(() => {
      const totalRevenue = (metricsData.lastSalesAmountSum.cumulative || 0)
        + currentDonations.value;
      const stickerRevenue = metricsData.lastSalesAmountSum.cumulative || 0;
      const donationRevenue = currentDonations.value;
      const socialShopRevenue = metricsData
        .lastSalesAmountSumFromSocialShops.cumulative || 0;

      return [
        {
          id: 'gross-revenue',
          label: 'Gross Revenue',
          value: formatCurrency(totalRevenue),
          meta: 'Sales + Donations',
        },
        {
          id: 'sticker-revenue',
          label: 'Sticker Revenue',
          value: formatCurrency(stickerRevenue),
          meta: calculatePercentageChange(
            metricsData.salesAmountSum,
          ) || 'All time',
        },
        {
          id: 'donation-revenue',
          label: 'Donation Revenue',
          value: formatCurrency(donationRevenue),
          meta: calculatePercentageChange(
            metricsData.donationsReceived,
          ) || 'All time',
        },
        {
          id: 'social-shop-revenue',
          label: 'Social Shop Revenue',
          value: formatCurrency(socialShopRevenue),
          meta: calculatePercentageChange(
            metricsData.salesAmountSumFromSocialShops,
          ) || 'All time',
        },
      ];
    });

    // Marketplace Analytics
    const marketplaceAnalytics = computed(() => {
      const socialShopProducts = currentSocialShopProducts.value;
      const socialShopSales = metricsData
        .lastSalesCountSumFromSocialShops.cumulative || 0;

      return [
        {
          id: 'social-shop-products',
          label: 'Social Shop Products',
          value: formatNumber(socialShopProducts),
          meta: calculatePercentageChange(
            metricsData.productsAddedToSocialShopsCount,
          ) || 'Active',
        },
        {
          id: 'social-shop-sales',
          label: 'Social Shop Sales',
          value: formatNumber(socialShopSales),
          meta: calculatePercentageChange(
            metricsData.salesCountSumFromSocialShops,
          ) || 'All time',
        },
      ];
    });

    // Recent Supporters with enhanced data
    const recentSupporters = computed(() => metricsData.recentSupporters
      .slice(0, 5).map((supporter) => ({
        ...supporter,
        initials: getInitials(supporter.purchaserName),
        timeAgo: getTimeAgo(supporter.transactionDate),
        description: `donated to ${supporter.stickerName}`,
        indicatorClass: 'indicator-teal',
      })));

    const onDateRangeChange = (dates: string[] | null) => {
      if (dates && dates.length === 2) {
        fetchMetrics(); // Refetch data with new date range
      }
    };

    // Navigate to Activity page
    const navigateToActivity = () => {
      if (isOrgAdmin.value && currentOrgId.value) {
        // Org admin: navigate to activity page with org ID
        router.push(`/activity/${currentOrgId.value}`);
      } else {
        // Super admin: navigate to activity page without org ID
        router.push('/activity');
      }
    };

    // Watch for organization changes
    watch(currentOrgId, (newOrgId) => {
      if (newOrgId) {
        fetchMetrics();
      }
    });

    return {
      // getter
      isAuthorizedUser,
      userName,

      // data
      metricsData,

      // computed data
      keyMetrics,
      communityStats,
      socialActivityStats,
      recentSupporters,
      transactionAnalytics,
      revenueAnalytics,
      marketplaceAnalytics,

      // current values
      currentWalletBalance,

      // date range
      dateRange,
      defaultDateRange,
      onDateRangeChange,

      // navigation
      navigateToActivity,

      // utility functions
      formatNumber,
      formatCurrency,
    };
  },
});
</script>

<style lang="scss" scoped>
// Color Variables - Exact from Figma/CMS Dashboard
$color-teal-bg: #c3eae3;
$color-teal-text: #005c6b;
$color-teal-accent: #01beb0;
$color-purple-bg: #e8e9fd;
$color-purple-text: #4349dd;
$color-yellow-bg: #fef7e0;
$color-yellow-text: #8a7800;
$color-yellow-accent: #ffdb0f;
$color-pink-bg: #ffe0ee;
$color-pink-text: #ff6b9d;
$color-blue-bg: #dde9f9;
$color-blue-text: #4876c9;
$color-orange-bg: #ffecd9;
$color-orange-text: #ea580c;

// Gray scale from design system
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-300: #d1d5db;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;

.dashboard-container {
  min-height: 100vh;
  padding: 0;
}

.dashboard-header {
  background-color: transparent;
  padding: 2rem;
  margin-bottom: 0.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
}

.welcome-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.welcome-title {
  font-size: 1.875rem;
  font-weight: 800;
  color: $gray-900;
  margin: 0;
  line-height: 1.2;
}

.welcome-subtitle {
  font-size: 1.125rem;
  color: $gray-600;
  margin: 0;
  line-height: 1.5;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.wallet-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: $gray-50;
  border-radius: 6px;
}

.wallet-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: $gray-500;
}

.wallet-amount {
  font-size: 1rem;
  font-weight: 700;
  color: $gray-900;
}

:deep(.el-date-editor) {
  --el-date-editor-width: 280px;
  --el-input-border-color: #d1d5db;
  --el-input-focus-border-color: $color-teal-accent;
  --el-input-hover-border-color: $color-teal-accent;

  .el-input__wrapper {
    border-color: #d1d5db;

    &:hover {
      border-color: $color-teal-accent;
    }

    &.is-focus {
      border-color: $color-teal-accent;
      box-shadow: 0 0 0 1px rgba(1, 190, 176, 0.2);
    }
  }
}

.loading-state,
.error-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  background-color: white;
  margin: 2rem;
  border-radius: 8px;
}

.loading-message,
.error-message {
  font-size: 1.2rem;
  font-weight: 600;
  color: $gray-600;
}

.error-message {
  color: #ef4444;
}

.dashboard-content {
  padding: 0 2rem 2rem 2rem;
}

// Key Metrics Grid - Exact from Figma
.key-metrics-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.25rem;
  margin-bottom: 1.5rem;
}

.key-metric-card {
  background-color: white;
  padding: 1.5rem;
  border-radius: 16px;
  border: 1px solid #dfdfdf;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.metric-icon-wrapper {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &.icon-teal {
    background-color: $color-teal-bg;
    .metric-icon {
      color: $color-teal-text;
    }
  }

  &.icon-purple {
    background-color: $color-purple-bg;
    .metric-icon {
      color: $color-purple-text;
    }
  }

  &.icon-yellow {
    background-color: $color-yellow-bg;
    .metric-icon {
      color: $color-yellow-text;
    }
  }

  &.icon-pink {
    background-color: $color-pink-bg;
    .metric-icon {
      color: $color-pink-text;
    }
  }

  &.icon-blue {
    background-color: $color-blue-bg;
    .metric-icon {
      color: $color-blue-text;
    }
  }

  &.icon-orange {
    background-color: $color-orange-bg;
    .metric-icon {
      color: $color-orange-text;
    }
  }
}

.metric-icon {
  font-size: 1.25rem;
  width: 1.25rem;
  height: 1.25rem;
  font-weight: 600;

  :deep(svg) {
    stroke-width: 2px;
    paint-order: stroke fill;
  }
}

.metric-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.metric-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: $gray-500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.metric-value-row {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.metric-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: $gray-900;
  line-height: 1;
}

.metric-change {
  font-size: 0.75rem;
  font-weight: 600;

  &.change-positive {
    color: $color-teal-text;
  }
}

// Two-Column Layout - Exact 2/3 - 1/3 split from Figma
.dashboard-main-layout {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;
}

.dashboard-left,
.dashboard-right {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

// Section Cards
.section-card {
  background-color: white;
  border-radius: 16px;
  border: 1px solid #dfdfdf;
  box-shadow: none;

  :deep(.el-card__body) {
    padding: 1.5rem;
  }
}

.section-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: $gray-900;
  margin: 0 0 1.25rem 0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;

  .section-title {
    margin: 0;
  }
}

.section-header-small {
  margin-bottom: 1.25rem;

  .section-title {
    margin: 0 0 0.25rem 0;
  }
}

.section-subtitle {
  font-size: 0.75rem;
  color: $gray-500;
  margin: 0;
}

.view-all-btn {
  color: $gray-500;
  font-weight: 500;

  &:hover {
    color: $gray-900;
  }
}

// Stats Grid (Community Overview) - 3 columns from Figma
.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

// Social Activity Grid - 4 columns
.social-stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.stat-icon-wrapper {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &.icon-teal {
    background-color: $color-teal-bg;
    .stat-icon {
      color: $color-teal-text;
    }
  }

  &.icon-purple {
    background-color: $color-purple-bg;
    .stat-icon {
      color: $color-purple-text;
    }
  }

  &.icon-yellow {
    background-color: $color-yellow-bg;
    .stat-icon {
      color: $color-yellow-text;
    }
  }

  &.icon-pink {
    background-color: $color-pink-bg;
    .stat-icon {
      color: $color-pink-text;
    }
  }

  &.icon-blue {
    background-color: $color-blue-bg;
    .stat-icon {
      color: $color-blue-text;
    }
  }

  &.icon-orange {
    background-color: $color-orange-bg;
    .stat-icon {
      color: $color-orange-text;
    }
  }
}

.stat-icon {
  font-size: 1rem;
  width: 1rem;
  height: 1rem;
  font-weight: 600;

  :deep(svg) {
    stroke-width: 2px;
    paint-order: stroke fill;
  }
}

.stat-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.stat-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: $gray-500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: $gray-900;
  line-height: 1;
}

// Activity List - Exact from Figma
.activity-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  border-top: 1px solid $gray-100;
}

.activity-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 0;
  border-bottom: 1px solid $gray-50;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: $gray-50;
  }
}

.activity-avatar {
  flex-shrink: 0;
}

.avatar-circle {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  background-color: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: $gray-600;
}

.activity-content {
  flex: 1;
  min-width: 0;
}

.activity-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.125rem;
}

.activity-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: $gray-900;
}

.activity-dot {
  width: 0.25rem;
  height: 0.25rem;
  border-radius: 50%;
  background-color: $gray-300;
}

.activity-time {
  font-size: 0.75rem;
  color: $gray-500;
}

.activity-description {
  font-size: 0.875rem;
  color: $gray-600;
}

.activity-amount {
  font-weight: 600;
  color: $gray-900;
}

.activity-indicator {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  flex-shrink: 0;

  &.indicator-teal {
    background-color: $color-teal-accent;
  }

  &.indicator-purple {
    background-color: $color-purple-text;
  }

  &.indicator-pink {
    background-color: $color-pink-text;
  }

  &.indicator-yellow {
    background-color: $color-yellow-accent;
  }

  &.indicator-blue {
    background-color: $color-blue-text;
  }
}

// Analytics Grids - From Figma
.analytics-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.analytics-grid-4 {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
}

.analytics-grid-5 {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
}

.analytics-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.analytics-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: $gray-500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.analytics-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: $gray-900;
  line-height: 1;
}

.analytics-meta {
  font-size: 0.75rem;
  font-weight: 600;

  &:contains('↑') {
    color: $color-teal-text;
  }

  &:contains('WoW'),
  &:contains('MoM'),
  &:contains('loads'),
  &:contains('wallets'),
  &:contains('sold'),
  &:contains('gifts'),
  &:contains('avg'),
  &:contains('active'),
  &:contains('reserve'),
  &:contains('now'),
  &:contains('time'),
  &:contains('Average'),
  &:contains('Upcoming'),
  &:contains('Per glow'),
  &:contains('Stickers') {
    color: $gray-500;
  }
}

// Performers List - Exact from Figma
.performers-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  border-top: 1px solid $gray-100;
}

.performer-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 0;
  border-bottom: 1px solid $gray-50;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: $gray-50;
  }
}

.performer-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
}

.performer-avatar {
  flex-shrink: 0;
}

.performer-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.performer-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: $gray-900;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.performer-amount {
  font-size: 0.75rem;
  color: $gray-500;
}

.performer-indicator {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  flex-shrink: 0;

  &.indicator-teal {
    background-color: $color-teal-accent;
  }

  &.indicator-purple {
    background-color: $color-purple-text;
  }

  &.indicator-pink {
    background-color: $color-pink-text;
  }

  &.indicator-yellow {
    background-color: $color-yellow-accent;
  }
}

// Stickers Grid
.stickers-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.sticker-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background-color: $gray-50;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;

  &:hover {
    background-color: $gray-100;
    border-color: $color-teal-accent;
  }
}

.sticker-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.sticker-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: $gray-900;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sticker-stats {
  font-size: 0.75rem;
  color: $gray-500;
}

.sticker-amount {
  font-size: 1rem;
  font-weight: 700;
  color: $color-teal-accent;
  white-space: nowrap;
  margin-left: 1rem;
}

// Referrals List
.referrals-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.referral-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.referral-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: $gray-700;
}

.referral-amount {
  font-size: 0.875rem;
  font-weight: 700;
  color: $gray-900;
}

// Responsive Design - Exact from Figma breakpoints
@media (max-width: 1400px) {
  .key-metrics-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .dashboard-main-layout {
    grid-template-columns: 1fr;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .social-stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .dashboard-header {
    flex-direction: column;
    align-items: flex-start;
    padding: 1.5rem;
  }

  .header-actions {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }

  .wallet-info {
    justify-content: space-between;
  }

  :deep(.el-date-editor) {
    --el-date-editor-width: 100%;
    width: 100%;
  }

  .dashboard-content {
    padding: 0 1rem 1rem 1rem;
  }

  .stickers-grid {
    grid-template-columns: 1fr;
  }

  .key-metrics-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .stats-grid,
  .social-stats-grid {
    grid-template-columns: 1fr;
  }

  .analytics-grid,
  .analytics-grid-4 {
    grid-template-columns: repeat(2, 1fr);
  }

  .welcome-title {
    font-size: 1.5rem;
  }

  .welcome-subtitle {
    font-size: 1rem;
  }

  .section-card {
    :deep(.el-card__body) {
      padding: 1rem;
    }
  }
}

@media (max-width: 480px) {
  .analytics-grid,
  .analytics-grid-4 {
    grid-template-columns: 1fr;
  }
}
</style>
